<template>
  <div v-if="!globalIsLoading">
    <mobile-header />
    <div class="main">
      <div class="sidebar-right print-hidden">
        <div class="sidebar-inner">
          <div class="sidebar-logo-wrap">
            <router-link :to="'/dashboard'">
              <img
                class="d-none d-xl-block mx-auto"
                src="@/assets/logos/av-rebrand-logo.svg"
                :alt="$t('avLogoAlt')"
                width="240"
              />
              <img
                class="d-sm-block d-md-block d-xl-none mx-auto mt-1"
                src="@/assets/logos/av-icon-black.svg"
                :alt="$t('avLogoAlt')"
                width="45"
              />
            </router-link>
          </div>
          <Menu class="sidebar-menu" />
        </div>
      </div>
      <div class="header-container print-hidden">
        <div class="header-inner">
          <desktop-header />
        </div>
      </div>
      <div class="body-content" :class="{ products: isProductSettingsPage }">
        <div class="content-container">
          <div class="mt-3 mt-md-4 mb-5">
            <slot />
          </div>
          <component :is="isProductSettingsPage ? 'HelpBar' : null" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

import Menu from '@/app/global/components/Menu.vue';
import MobileHeader from '@/app/global/components/MobileHeader.vue';
import DesktopHeader from '@/app/global/components/DesktopHeader.vue';
import log from '@/service/log';
import HelpBar from '@/app/productSettings/components/HelpBar.vue';

export default {
  name: 'Authenticated',
  components: {
    Menu,
    MobileHeader,
    DesktopHeader,
    HelpBar,
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapGetters({
      currentBusinessProducts: 'business/currentBusinessProducts',
    }),
    ...mapState({
      currentBusinessUserRole: state => state.user.currentBusinessUserRole,
      globalIsLoading: state => state.globalIsLoading,
    }),
    userRolesForRoute() {
      return this.$route.meta.authorize ? this.$route.meta.authorize : null;
    },
    productForRoute() {
      return this.$route.meta.product ? this.$route.meta.product : null;
    },
    isProductSettingsPage() {
      return this.$route.name === 'productSettings' || this.$route.name === 'autoVerifyResearchSuite';
    },
  },
  beforeCreate() {
    this.$store.commit('SET_GLOBAL_LOADING', true);
  },
  created() {
    document.body.classList.add('authenticated');
    // Starting point for logged in app
    this.getUser();
  },
  unmounted() {
    document.body.classList.remove('authenticated');
  },
  watch: {
    // Check user permissions on route changes
    $route() {
      this.checkUserRole();
    },
  },
  methods: {
    async getUser() {
      try {
        // Make sure they are logged in
        await Promise.all([this.$store.dispatch('user/fetchAndSetUser'), this.getBusinesses()]);
      } catch (e) {
        // This should not happen
        // Means we are logged in with auth0
        // but our system is failing to get user data
        // Log the user out and go to error screen
        log(e);
        await this.$store.dispatch('user/logout', {
          auth: this.$auth,
          redirect: `${window.location.origin}/auth-error`,
        });
      }
    },
    async getBusinesses() {
      try {
        // Get users businesses
        await this.$store.dispatch('user/fetchAndSetBusinesses');
        await this.setCurrentBusiness();
      } catch (e) {
        // The user has no businesses - expected state, not an error worth logging
        await this.$store.dispatch('user/logout', {
          auth: this.$auth,
          redirect: `${window.location.origin}/business-error`,
        });
      }
    },
    async setCurrentBusiness() {
      try {
        // Get current business from session
        await this.$store.dispatch('user/fetchAndSetCurrentBusinessFromSession');
        this.$store.commit('user/AUTH_STATUS', true);
        await this.checkUserRole();
      } catch (e) {
        // If not in session just set the first one
        try {
          await this.$store.dispatch('user/setDefaultBusiness');
          this.$store.commit('user/AUTH_STATUS', true);
          await this.checkUserRole();
        } catch (error) {
          log(error);
        }
      }
    },
    async checkUserRole() {
      // If the user doesn't have the proper role for the route
      // redirect to /dashboard
      if (
        !this.userRolesForRoute.includes(this.currentBusinessUserRole) ||
        (this.productForRoute && !this.currentBusinessProducts.includes(this.productForRoute))
      ) {
        this.$router.push({ path: '/dashboard' }, () => {
          this.$store.commit('SET_GLOBAL_LOADING', false);
        });
      } else {
        // All good, lets allow them access!
        this.$store.commit('SET_GLOBAL_LOADING', false);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';
// General layout

// Desktop layout
.main {
  position: relative;
  .sidebar-right {
    position: fixed;
    height: 100%;
    width: 320px;
    background-color: #fff;
    z-index: 100;
  }
  .header-container {
    position: fixed;
    width: 100%;
    padding-left: 320px;
    background-color: #fff;
    z-index: 50;
    border-bottom: 1.5px solid $hr-color;
    .header-inner {
      padding: 20px 50px 20px 82px;
    }
  }
  .body-content {
    max-width: 1100px;
    margin-left: 320px;
    .content-container {
      padding: 110px 82px 110px 82px;
    }
  }
}

// Tablet
@media screen and (max-width: 1200px) {
  .main {
    .sidebar-right {
      width: 100px;
    }
    .body-content {
      margin-left: 100px;
      .content-container {
        padding: 90px 30px 30px;
      }
    }
    .header-container {
      padding-left: 80px;
      .header-inner {
        padding: 20px 50px 20px;
      }
    }
  }
}

// Mobile
@media screen and (max-width: 767px) {
  .main {
    .header-container,
    .sidebar-right {
      display: none;
    }
    .body-content {
      margin-left: 0;
      .content-container {
        padding: 20px 15px;
      }
    }
  }
}

// Sidebar
.sidebar-right {
  border-right: 1.5px solid $hr-color;
  @media screen and (min-width: 768px) {
    display: block;
    animation: slideInFromLeft 0.25s;
  }
}
@keyframes slideInFromLeft {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

.sidebar-menu {
  margin-top: 30px;
}

.sidebar-logo-wrap {
  padding: 20px;
  @media screen and (max-width: 1200px) {
    padding: 20px 15px;
  }
  a {
    display: block;
    text-align: center;
  }
  .sidebar-desktop-logo {
    max-width: 100%;
    height: auto;
  }
}

// Print
@media print {
  .main {
    .body-content {
      margin-left: 0;
      max-width: 100%;
      .content-container {
        padding: 0;
      }
    }
  }
}
</style>
